import React, { useCallback, useEffect } from "react";
import {
  RegenerationType,
  RegenerationArgs,
  LoadingOperation,
  WebSocketResponse,
} from "./types";

interface UseNarrativeRegenerationProps {
  liveWebSocket?: React.MutableRefObject<WebSocket | null>;
  sessionSocket: WebSocket | null;
  narrativeIndex: number;
  narrativePath: string;
  showError?: (message: string) => void;
  setLoadingOperation: (op: LoadingOperation) => void;
  chatSendMessage?: (message: string) => Promise<void>;
  setShowChat?: (show: boolean) => void;
}

export const useNarrativeRegeneration = ({
  liveWebSocket,
  sessionSocket,
  narrativeIndex,
  narrativePath,
  showError,
  setLoadingOperation,
  chatSendMessage,
  setShowChat,
}: UseNarrativeRegenerationProps) => {
  /**
   * @name handleNarrativeRegeneration
   *
   * @description Sends a message to the chat panel to regenerate narrative based on the operation type
   */
  const handleNarrativeRegeneration = useCallback(
    async (
      regenerationType: RegenerationType,
      regenerationArgs: RegenerationArgs | null
    ) => {
      // A confirmation can outlive its editing session. Do not submit an AI
      // edit over the separate chat transport after the editing socket closes.
      if (liveWebSocket && liveWebSocket.current?.readyState !== WebSocket.OPEN) {
        setLoadingOperation(null);
        return;
      }
      // Validate chat function is available
      if (!chatSendMessage) {
        showError?.(
          "Chat is not available. Please ensure the AI Assistant panel is enabled."
        );
        setLoadingOperation(null);
        return;
      }

      try {
        // Build the message based on regeneration type
        let messageText: string;
        if (regenerationType === "CONDENSE") {
          messageText = `${narrativePath} Please condense the narrative to make it less wordy while preserving all important details.`;
        } else if (regenerationType === "EXPATIATE") {
          messageText = `${narrativePath} Please expand the narrative to make it more detailed and comprehensive.`;
        } else if (
          regenerationType === "ADD_CONTEXT" &&
          regenerationArgs?.additionalContext
        ) {
          messageText = `${narrativePath} ${regenerationArgs.additionalContext}`;
        } else if (regenerationType === "REPLACE" && regenerationArgs) {
          const oldText = regenerationArgs.oldText || regenerationArgs.oldStr;
          const newText = regenerationArgs.newText || regenerationArgs.newStr;
          messageText = `${narrativePath} Please replace "${oldText}" with "${newText}" in the narrative.`;
        } else if (regenerationType === "TO_FIRST_PERSON") {
          messageText = `${narrativePath} Please convert this narrative from third-person to first-person perspective. Convert references to the officer to first-person pronouns ("I", "me", "my"). For any subjects or persons mentioned in the narrative, use their actual names (e.g., "Susie", "Ms Stern") instead of generic terms like "the subject". The AI should determine the appropriate name format based on context. Keep any direct quotes from other people unchanged. Maintain the professional tone and factual content.`;
        } else if (
          regenerationType === "TO_THIRD_PERSON" &&
          regenerationArgs?.officerName
        ) {
          messageText = `${narrativePath} Please convert this narrative from first-person to third-person perspective. Replace first-person pronouns as follows: "I" -> "${regenerationArgs.officerName}", "me" -> "${regenerationArgs.officerName}", "my" and "mine" -> "${regenerationArgs.officerName}'s", "myself" -> "${regenerationArgs.officerName}". Always use the officer's actual name (e.g., "Capt Favii") throughout - avoid generic terms like "the officer" as they are too vague. For any subjects or persons mentioned, use their actual names from the report instead of generic terms like "the subject". Keep any direct quotes from other people unchanged. Maintain the professional tone and factual content.`;
        } else {
          showError?.("Invalid regeneration request.");
          setLoadingOperation(null);
          return;
        }

        // Show chat panel if hidden
        if (setShowChat) {
          setShowChat(true);
        }

        // Send message through chat - the chat handles streaming and data refresh
        await chatSendMessage(messageText);

        // Clear loading state after message is sent (chat will handle the rest)
        setLoadingOperation(null);
      } catch (error) {
        console.error("Error regenerating narrative:", error);
        showError?.(
          error instanceof Error
            ? error.message
            : "Failed to regenerate narrative. Please try again."
        );
        setLoadingOperation(null);
      }
    },
    [
      liveWebSocket,
      narrativePath,
      showError,
      setLoadingOperation,
      chatSendMessage,
      setShowChat,
    ]
  );

  /**
   * Effect to set up WebSocket message listener for narrative regeneration responses
   */
  useEffect(() => {
    const ws = sessionSocket;
    if (!ws) return;

    const handleMessage = (event: MessageEvent) => {
      // The ref changes synchronously; effect cleanup may run after a queued
      // message from the previous editing session has already arrived.
      if (liveWebSocket?.current !== ws) return;
      let data: WebSocketResponse;
      try {
        data = JSON.parse(event.data);
      } catch {
        return; // Ignore malformed messages
      }

      // The session owner displays terminal errors and redirects once.
      if (data.closing) return;

      // Handle error field from envelope
      if (data.error) {
        showError?.(data.error);
        // Always reset loading states on error
        setLoadingOperation(null);
        return;
      }

      // Handle success field - check for narrative regeneration success
      if (data.success === "Narrative regenerated successfully") {
        setLoadingOperation(null);
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [sessionSocket, liveWebSocket, showError, narrativeIndex, setLoadingOperation]);

  return { handleNarrativeRegeneration };
};
